// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import { getCurrentUserCountry } from './countryDetection';

export const isLoopbackHostname = hostname => {
  if (!hostname || typeof hostname !== 'string') {
    return false;
  }
  const host = hostname.toLowerCase().split(':')[0];
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
};

/**
 * yarn dev: the browser must call the page origin (webpack proxies /api to
 * port 3500). Never return http://localhost:3500 when `origin` is set — Chrome
 * blocks public HTTPS → loopback, and Flex cookies are host-scoped.
 */
export const resolveYarnDevApiBaseUrl = ({ port, origin }) => {
  if (origin) {
    return origin;
  }
  return `http://localhost:${port}`;
};

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return resolveYarnDevApiBaseUrl({
        port,
        origin: window.location.origin,
      });
    }
    return `http://localhost:${port}`;
  }
  //  return `http://localhost:4000`;

  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  // Helper to check if a URL is localhost
  const isLocalhostURL = url => {
    if (!url) return false;
    // Match http://localhost, http://localhost:port, http://127.0.0.1, etc.
    // Also match any URL containing localhost or 127.0.0.1
    const urlLower = url.toLowerCase();
    return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(url) || 
           urlLower.includes('localhost') || 
           urlLower.includes('127.0.0.1');
  };

  // Helper to clean URL
  const cleanURL = url => url ? url.replace(/\/$/, '') : '';

  // Determine the root URL to use (from parameter or env var)
  const rootURL = marketplaceRootURL || process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  const cleanedRootURL = cleanURL(rootURL);

  // In the browser, ALWAYS reject localhost URLs and use window.location.origin instead
  // This prevents issues where env vars might be incorrectly set to localhost
  if (isBrowser) {
    // If we have a localhost URL (from parameter or env), ALWAYS use window.location.origin
    if (isLocalhostURL(cleanedRootURL)) {
      const origin = window.location.origin;
      const originIsLocalhost = isLocalhostURL(origin);
      // Only warn if we're not actually on localhost
      if (!originIsLocalhost) {
        // eslint-disable-next-line no-console
        console.warn(
          `[apiBaseUrl] Rejecting localhost URL "${cleanedRootURL}", using window.location.origin instead:`,
          origin
        );
      }
      return origin;
    }
    // If we have a valid non-localhost URL, use it
    if (cleanedRootURL) {
      return cleanedRootURL;
    }
    // Fallback to window.location.origin
    return window.location.origin;
  }

  // For SSR (server-side rendering), use the provided URL or env var
  // This is safe because SSR happens on the server where localhost is valid
  if (cleanedRootURL) {
    return cleanedRootURL;
  }
  
  // For SSR fallback, return empty string
  return '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

const createRequestId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // ignore
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // Transit endpoints: serialize object body. JSON endpoints (e.g. auth0/flex-session): pass body through.
  const contentTypeHeader = headers?.['Content-Type']?.split(';')[0];
  const useTransitBody =
    (!headers || contentTypeHeader === 'application/transit+json') && body != null;
  let bodyForFetch;
  if (useTransitBody) {
    bodyForFetch = serialize(body);
  } else if (body != null) {
    bodyForFetch = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // Get current user country for service fee calculation
  const country = getCurrentUserCountry();
  
  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: {
      ...(headers || { 'Content-Type': 'application/transit+json' }),
      'X-User-Country': country.countryCode,
      'X-Request-Id': createRequestId(),
    },
    ...(bodyForFetch !== undefined ? { body: bodyForFetch } : {}),
    ...rest,
  };

  return window
    .fetch(url, fetchOptions)
    .then(res => {
    const requestId = res.headers.get('X-Request-Id');
    const checkoutDiagnosticHeader = res.headers.get('X-FYK-Checkout-Diagnostic');

    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      // Handle error responses based on content type
      if (contentType === 'application/transit+json') {
        return res.text().then(text => {
          const errorData = deserialize(text);
          let e = new Error();
          e = Object.assign(e, errorData);
          // Ensure HTTP status is always set (transit body may omit it; duck + storableError rely on it)
          if (e.status == null) {
            e.status = res.status;
          }
          if (e.statusText == null) {
            e.statusText = res.statusText;
          }
          if (requestId) {
            e.requestId = requestId;
          }
          if (checkoutDiagnosticHeader) {
            e.checkoutDiagnosticCode = checkoutDiagnosticHeader;
          }
          throw e;
        });
      } else if (contentType === 'application/json') {
        return res.json().then(data => {
          const message = data?.error || data?.message;
          const e = new Error(message || res.statusText || 'Request failed');
          e.status = data?.status ?? res.status;
          e.statusText = data?.statusText ?? res.statusText;
          if (message) e.message = message;
          if (data?.error != null) e.error = data.error;
          if (requestId) {
            e.requestId = requestId;
          }
          if (checkoutDiagnosticHeader) {
            e.checkoutDiagnosticCode = checkoutDiagnosticHeader;
          }
          if (data?.diagnostic) {
            e.checkoutDiagnostic = data.diagnostic;
          }
          // Marketplace / server error bodies: data.errors, top-level errors[], or initiate-privileged `details`
          if (data?.data && typeof data.data === 'object') {
            e.data = data.data;
          } else if (Array.isArray(data?.errors)) {
            e.data = { errors: data.errors };
          } else if (data?.details != null) {
            const d = data.details;
            if (Array.isArray(d)) {
              e.data = { errors: d };
            } else if (d && typeof d === 'object' && Array.isArray(d.errors)) {
              e.data = d;
            } else if (d && typeof d === 'object' && (d.code || d.detail)) {
              e.data = { errors: [d] };
            }
          }
          throw e;
        });
      } else {
        // Fallback for other content types (e.g. text/html 404 or 500 pages)
        return res.text().then(text => {
          const isHtml = contentType === 'text/html' || (text && text.trim().startsWith('<'));
          const message = isHtml
            ? `HTTP ${res.status} ${res.statusText || 'Error'}`
            : (text || `HTTP ${res.status} ${res.statusText}`);
          let e = new Error(message);
          e.status = res.status;
          e.statusText = res.statusText;
          if (requestId) {
            e.requestId = requestId;
          }
          if (checkoutDiagnosticHeader) {
            e.checkoutDiagnosticCode = checkoutDiagnosticHeader;
          }
          throw e;
        });
      }
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const checkCouponBalance = body => {
  return post('/api/checkCouponBalance', body);
};

export const getVouchersByEmail = email => {
  return post('/api/getVouchersByEmail', { email });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Exchange Auth0 ID token for Sharetribe Flex session (cookies). See server/api/auth/auth0FlexSession.js
export const completeAuth0FlexSession = body => {
  return request('/api/auth/auth0/flex-session', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Send Auth0 Passwordless OTP to email (in-app flow). See server/api/auth/auth0PasswordlessOtp.js */
export const auth0PasswordlessStart = body => {
  return request('/api/auth/auth0/passwordless/start', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Exchange email + OTP for Auth0 id_token (in-app flow). */
export const auth0PasswordlessVerify = body => {
  return request('/api/auth/auth0/passwordless/verify', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Update user status using admin privileges
//
// This endpoint allows super users to change the status of other users.
// Valid statuses: 'active', 'pending-approval', 'banned', 'suspended', 'inactive'
//
// See `server/api/admin-providers.js` to see what data should be sent in the body.
export const updateUserStatus = (userId, status) => {
  return request(`/api/admin-providers/${userId}/status`, {
    method: methods.PUT,
    body: { status },
  });
};

// Get user status using admin privileges
//
// This endpoint allows super users to get the current status of other users.
//
// See `server/api/admin-providers.js` to see the response format.
export const getUserStatus = userId => {
  return request(`/api/admin-providers/${userId}/status`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const sendContactEmail = ({ name, subject, message, recipientEmail }) => {
  return request('/api/send-contact-email', {
    method: methods.POST,
    body: { name, subject, message, recipientEmail },
  });
};

export const markVoucherDelivered = body => {
  return request('/api/mark-voucher-delivered', {
    method: methods.POST,
    body,
  });
};

/**
 * Voucher finalization: run confirm-payment server-side (trusted token exchange) and persist Stripe PI id to Integration metadata.
 * This prevents “Stripe succeeded but transaction later expired” when the browser loses auth/network mid-flow.
 */
export const voucherFinalizePayment = body => {
  return post('/api/voucher-finalize-payment', body);
};

/** Server-side provider requirements check — returns outcomes only (no personal data). */
export const checkProviderRequirements = ({ userId }) => {
  return request('/api/provider-requirements/check', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
};

// Provider Connect account id for Stripe Payment Request Button (on_behalf_of).
// See `server/api/provider-stripe-account.js`.
export const getProviderStripeAccount = listingId => {
  return request('/api/provider-stripe-account', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingId }),
  });
};

/**
 * Feature flags API: read runtime feature flags (Google Pay, Apple Pay toggles).
 */
export const getFeatureFlagsApi = ({ refresh = false } = {}) => {
  const query = refresh ? '?refresh=true' : '';
  return request(`/api/feature-flags${query}`, {
    method: methods.GET,
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });
};

/**
 * Feature flags API: update runtime feature flags (Super Admin only).
 */
export const updateFeatureFlagsApi = featureFlags => {
  return request('/api/feature-flags', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ featureFlags }),
  });
};



